import React from 'react'
import { colors, radius, font } from './tokens'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  fullWidth?: boolean
  loading?: boolean
}

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  sm: { padding: '6px 10px', fontSize: font.size.sm },
  md: { padding: '8px 14px', fontSize: font.size.base },
  lg: { padding: '10px 18px', fontSize: font.size.md },
}

const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    background: colors.accent,
    color: '#fff',
    border: '1px solid transparent',
  },
  secondary: {
    background: colors.surface,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
  },
  danger: {
    background: colors.danger,
    color: '#fff',
    border: '1px solid transparent',
  },
  ghost: {
    background: 'transparent',
    color: colors.textSecondary,
    border: '1px solid transparent',
  },
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth,
      loading,
      disabled,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading
    return (
      <button
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          borderRadius: radius.md,
          fontWeight: font.weight.medium,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.6 : 1,
          width: fullWidth ? '100%' : undefined,
          transition: 'background-color 0.12s, color 0.12s, border-color 0.12s',
          ...sizeStyles[size],
          ...variantStyles[variant],
          ...style,
        }}
        {...rest}
      >
        {loading && (
          <span
            aria-hidden="true"
            style={{
              width: '12px',
              height: '12px',
              border: '2px solid rgba(255,255,255,0.4)',
              borderTopColor: 'currentColor',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.7s linear infinite',
            }}
          />
        )}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
